import { motion } from "framer-motion";
import useInView from "../hooks/useInView";
import { CONTACT_EMAIL, INSTAGRAM_URL, YOUTUBE_URL, REEL_URL } from "../lib/films";
import { Instagram, Youtube, Film as FilmIcon, ArrowUpRight } from "lucide-react";

const MARQUEE_LINES = [
  "You are going to be okay.",
  "✷",
  "Stay soft.",
  "✷",
  "The light is on.",
  "✷",
  "Everything is going to be okay.",
  "✷",
];

export const Contact = () => {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="relative bg-bg overflow-hidden border-t border-white/[0.05]"
    >
      {/* End-credits marquee */}
      <div className="relative overflow-hidden border-b border-white/10 py-6 bg-bg">
        <div className="marquee-slow flex whitespace-nowrap gap-14 font-display italic text-4xl md:text-6xl text-ink/60">
          {Array.from({ length: 4 })
            .flatMap(() => MARQUEE_LINES)
            .map((t, i) => (
              <span key={i} className={t === "✷" ? "text-sun" : ""}>
                {t}
              </span>
            ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-14 relative">
        <div className="flex items-center gap-3 mb-16 font-mono text-[11px] tracking-widest2 uppercase text-muted">
          <span className="w-10 h-px bg-sun" />
          <span className="text-sun">(05)</span>
          <span>Get in touch</span>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.1 }}
          className="font-display italic text-3xl md:text-5xl lg:text-6xl text-ink/85 max-w-3xl leading-[1.05] mb-14"
        >
          Have a story that needs to find its hour? Reach out — I read every note.
        </motion.p>

        <motion.a
          data-testid="contact-email-link"
          href={`mailto:${CONTACT_EMAIL}`}
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.3, delay: 0.15 }}
          className="group inline-flex flex-wrap items-start gap-3 font-display italic text-sun leading-[0.9] tracking-tight hover:text-ink hover-fade"
          style={{ fontSize: "clamp(2.5rem, 8vw, 10rem)" }}
        >
          <span className="lowercase break-words max-w-full">{CONTACT_EMAIL}</span>
          <ArrowUpRight
            className="mt-3 shrink-0 group-hover:translate-x-2 group-hover:-translate-y-2 hover-fade"
            size={44}
          />
        </motion.a>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-white/10 pt-12">
          <div data-testid="contact-representation">
            <div className="font-mono text-[10px] tracking-widest2 uppercase text-muted mb-3">
              Representation
            </div>
            <div className="font-display italic text-2xl text-ink">Currently seeking.</div>
            <div className="font-mono text-[10px] tracking-widest2 uppercase text-muted mt-2">
              Direct inquiries welcome
            </div>
          </div>

          <div data-testid="contact-inquiries">
            <div className="font-mono text-[10px] tracking-widest2 uppercase text-muted mb-3">
              Press · Festivals · Collab
            </div>
            <a
              data-testid="contact-press-link"
              href={`mailto:${CONTACT_EMAIL}?subject=Hello%20Jy`}
              className="font-display italic text-2xl text-ink link-sweep lowercase"
            >
              {CONTACT_EMAIL}
            </a>
          </div>

          <div data-testid="contact-socials">
            <div className="font-mono text-[10px] tracking-widest2 uppercase text-muted mb-3">
              Elsewhere
            </div>
            <div className="flex items-center gap-6">
              <a
                data-testid="social-instagram"
                href={INSTAGRAM_URL}
                aria-label="Instagram"
                className="text-ink hover:text-sun hover-fade"
              >
                <Instagram size={22} />
              </a>
              <a
                data-testid="social-youtube"
                href={YOUTUBE_URL}
                aria-label="YouTube"
                className="text-ink hover:text-sun hover-fade"
              >
                <Youtube size={22} />
              </a>
              <a
                data-testid="social-vimeo"
                href={REEL_URL}
                aria-label="Vimeo Reel"
                className="text-ink hover:text-sun hover-fade"
              >
                <FilmIcon size={22} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 font-mono text-[11px] tracking-widest2 uppercase text-muted">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} JY TRISTIAN WRIGHT — ALL STORIES RESERVED
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sun animate-pulse" />
            <span>Available — 2026</span>
          </div>
        </div>
      </div>
    </section>
  );
};
